import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useLicenceAllowsCreate } from '@/lib/licence/useStageLicence.ts'
import { isCreatableIssueType } from '@/routes/projectStages/issues/lib/lastUsedIssueType.ts'
import {
  isIssueDetailRoute,
  STAGE_ROUTE_PATTERN,
} from '@/routes/projectStages/lib/newIssueEntryRoutes.ts'
import { isQuickCapturePhone } from '@/lib/quickCapture/isQuickCapturePhone.ts'
import { useQuickCaptureEnabled } from '@/lib/quickCapture/useQuickCaptureEnabled.ts'

/** Mirrors the `quick-capture` child of the stage route in `routes.tsx`. */
export const QUICK_CAPTURE_ROUTE_PATTERN = `/project_stages/:projectStageId/quick-capture`

export function quickCapturePath(projectStageId: string): string {
  return `/project_stages/${projectStageId}/quick-capture`
}

/**
 * The stage id on the screens that offer the quick-capture entry, else null: every stage screen
 * except the issue detail (which shows no FAB) and the capture screen itself.
 */
export function matchQuickCaptureEntryRoute(pathname: string): string | null {
  const projectStageId = matchPath(STAGE_ROUTE_PATTERN, pathname)?.params.projectStageId
  if (!projectStageId) return null
  if (isIssueDetailRoute(pathname)) return null
  if (matchPath(QUICK_CAPTURE_ROUTE_PATTERN, pathname) !== null) return null
  return projectStageId
}

type QuickCaptureEntryOptionsT = {
  /** False where the entry cannot be shown anyway (the desktop layout), so no read is spent. */
  enabled?: boolean
}

/**
 * The quick-capture entry point on the stage screens of a phone. `available` needs all of: a
 * phone (the same predicate the capture route's loader applies), the stage store holding the
 * route's stage, the licence allowing new content, at least one issue type the user may create
 * here (the global "Report" entry's preconditions), and the stage's pilot flag. The flag is read
 * fresh on every screen entry while online and the entry stays closed until that read settles;
 * offline the cached flag decides. `open` goes to the capture screen, whose loader checks the
 * flag again.
 */
export function useQuickCaptureEntry({ enabled = true }: QuickCaptureEntryOptionsT = {}) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const routeStageId = matchQuickCaptureEntryRoute(pathname)
  const currProjectStage = useProjectStageStore(s => s.currProjectStage)
  const issueTypes = useProjectStageStore(s => s.issueTypes)
  const licenceAllowsCreate = useLicenceAllowsCreate()

  // Right after a stage switch the store still holds the previous stage, whose issue types say
  // nothing about this one.
  const storeHoldsRouteStage =
    routeStageId !== null && !!currProjectStage && String(currProjectStage.id) === routeStageId
  const mayCreate =
    storeHoldsRouteStage && licenceAllowsCreate && issueTypes.some(isCreatableIssueType)
  // Checked last: a layout without the entry never asks the platform, nor the server.
  const eligible = enabled && mayCreate && isQuickCapturePhone()

  const flag = useQuickCaptureEnabled(routeStageId ?? '', {
    entryKey: pathname,
    enabled: eligible,
  })
  const available = eligible && flag.enabled

  function open() {
    if (!available || routeStageId === null) return
    navigate(quickCapturePath(routeStageId))
  }

  return { available, open }
}
